import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback } from 'react';
import { AlertTriangle, BarChart3, Plus } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n } from '../lib/i18n';
import { formatDate, formatMoney, formatPercent, isoDate } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingBlock,
  PageHeader,
  ProgressBar,
} from '../components/ui';

/**
 * Route: `/budgets`. A budget has no page of its own: a notification about one
 * (its alert threshold reached) opens the list with it in view and marked —
 * `?budget=<id>` (2026-10-11).
 */
export function BudgetsPage() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focus = params.get('budget');
  const intoView = useCallback((el: HTMLDivElement | null) => el?.scrollIntoView({ block: 'center' }), []);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['budgets'],
    queryFn: () => api.get<any[]>('/budgets'),
  });

  return (
    <>
      <PageHeader
        title={t('budgets.title')}
        action={
          can(P.FINANCE_BUDGETS_MANAGE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/budgets/new')}>
              {t('budgets.new')}
            </Button>
          )
        }
      />

      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.length ? (
        <Card><EmptyState icon={<BarChart3 className="h-8 w-8" />} /></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {data.map((budget) => {
            const usage = budget.usage;
            const tone = usage.limitExceeded ? 'error' : usage.thresholdExceeded ? 'warning' : 'success';
            return (
              <Card key={budget.id} className={clsx(budget.id === focus && 'ring-2 ring-primary/50')}>
                <div ref={budget.id === focus ? intoView : undefined} className="mb-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-h3 text-[var(--text-primary)]">{budget.name}</h3>
                    <p className="text-caption text-muted">
                      {enumLabel(budget.scopeType, locale)}
                      {budget.expenseCategory && ` · ${localized(budget.expenseCategory)}`}
                      {' · '}
                      {formatDate(budget.startDate, locale)} — {formatDate(budget.endDate, locale)}
                    </p>
                  </div>
                  {usage.limitExceeded ? (
                    <Badge tone="error" dot>{t('budgets.overBudget')}</Badge>
                  ) : usage.thresholdExceeded ? (
                    <Badge tone="warning" dot>{t('budgets.thresholdHit')}</Badge>
                  ) : (
                    <Badge tone="success">{formatPercent(usage.percentUsed, locale)}</Badge>
                  )}
                </div>

                <div className="mb-2 flex items-baseline justify-between gap-3">
                  <span className="text-metric tabular text-[var(--text-primary)]">
                    {formatMoney(usage.actualSpent, usage.currency, locale)}
                  </span>
                  <span className="text-body-sm tabular text-muted">
                    {t('common.of')} {formatMoney(usage.amount, usage.currency, locale)}
                  </span>
                </div>

                <ProgressBar percent={usage.percentUsed} tone={tone} />

                <div className="mt-3 flex items-center justify-between gap-3 text-body-sm">
                  <span className="text-muted">
                    {t('budgets.remaining')}:{' '}
                    <span className={usage.remaining < 0 ? 'text-error-fg dark:text-error-dark tabular' : 'tabular'}>
                      {formatMoney(usage.remaining, usage.currency, locale)}
                    </span>
                  </span>
                  {usage.alertThresholdPercent !== null && (
                    <span className="text-caption text-faint">
                      {t('budgets.threshold')} {usage.alertThresholdPercent}%
                    </span>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

    </>
  );
}
